// Which strategy to follow today, ranked against the market.
//
// The pad prints what each strategy thinks. This takes every strategy's
// tickets, run over the same board with the same context, and ranks them
// against one another on a single test: how much better the price your
// bookmaker is offering is than the price the rest of the market calls fair.
//
// NOT EACH MODEL'S OWN EV. The obvious ranking is `multi.ev` — the strategy's
// own probability times the price it is taking — and it is the wrong one. That
// number rewards whichever model disagrees with the market most, because a
// model that is badly wrong reports a wonderful edge; the backtests say those
// are exactly the ones not to follow. So every ticket is scored against the
// same outside opinion, with the same devig, whatever probability its own
// strategy was working from:
//
//     valueVsMarket = pMarket x price - 1
//
// +0.04 means your book pays 4% more than the market thinks the ticket is
// worth. It is the MARKET's expected value of your ticket, not the strategy's,
// and that is the whole point — one yardstick, held by someone else.
//
// The market view of a leg is derived the way `runStrategy` derives it, out of
// the same two functions, so a leg cannot be worth one thing on the pad and
// another here: the book named in Settings taken whole (net of commission
// where it charges any), or the rest of the board with the book being bet at
// left out of its own vote.
//
// BEST VALUE IS NOT MOST LIKELY TO WIN. `chance` rides along on every pick for
// that reason — the screen has to print both or it is selling long odds.

import { consensus } from './universe.js';
// Not the model — one function off it, the same one `runStrategy` borrows so a
// named market book reads identically in both places, commission and all.
import { bookFairNet } from './models/sharpAnchor.js';

/**
 * One devigging method for every ticket on the screen.
 *
 * A strategy picks its own — Value Favourites reads Shin, Optimal Legs reads
 * power — and that is right for deciding whether a leg is worth backing. It is
 * wrong for ranking, where four strategies are being compared: a ticket cannot
 * be allowed to place higher because its author preferred a kinder way of
 * stripping the margin.
 */
const DEVIG = 'power';

const SIDES = ['home', 'away', 'draw'];

/**
 * Every match in the universe under both names a leg can carry: the engine
 * writes `matchKey`, and Classic's legs — which come from `lib/tickets.js` —
 * only ever carried the provider's event id.
 *
 * Match keys are laid down first and an event id never overwrites one, so a
 * provider id that happens to read like somebody else's match key cannot
 * quietly repoint a leg at the wrong game.
 *
 * MORE THAN ONE BOARD, because the app has more than one: the racing card is a
 * universe of its own and the racing strategies' tickets are ranked against
 * the market on exactly the same test as everything else. Pass an array and
 * they are indexed together; the keys carry the sport, so nothing can collide.
 */
function indexMatches(universe) {
  const boards = Array.isArray(universe) ? universe : [universe];
  const matches = boards.flatMap((u) => [...(u?.matches?.values?.() ?? [])]);
  const byKey = new Map();
  for (const match of matches) byKey.set(match.matchKey, match);
  for (const match of matches) {
    const id = match.providerEventId;
    if (id && !byKey.has(id)) byKey.set(id, match);
  }
  return byKey;
}

/** The match a leg is on, or null when it is no longer on the board. */
function matchFor(leg, byKey) {
  return (leg?.matchKey ? byKey.get(leg.matchKey) : null)
    ?? (leg?.eventId ? byKey.get(leg.eventId) : null)
    ?? null;
}

/**
 * Which side of the fixture a leg backed. Engine and Classic legs both carry
 * `side`; the team name is the fallback, read off the universe the same way
 * `strategies/classic.js` reads it, for a leg written before `side` existed.
 */
function sideOf(leg, match) {
  if (SIDES.includes(leg?.side)) return leg.side;
  // A RACE HAS ITS OWN SIDES and there are as many of them as there are
  // runners, so there is no list to check against — only the match itself,
  // which knows whether `r4` is one of its runners. A leg naming a runner that
  // is no longer in the field (a late scratching) answers null and the ticket
  // drops out of the ranking, which is the right answer: it cannot be scored.
  if (leg?.side && match?.runners?.[leg.side]) return leg.side;
  const team = String(leg?.selection ?? '').replace(/ to win$/, '');
  if (!team) return null;
  if (team === match?.home?.name) return 'home';
  if (team === match?.away?.name) return 'away';
  return null;
}

/**
 * The book a leg's price was taken at. Classic's legs name no book at all —
 * they are priced at whichever one the risk profile names — so the context
 * answers for them.
 */
function bookOf(leg, ctx) {
  return leg?.bookmakerKey ?? leg?.bookmaker ?? ctx?.bookmaker ?? null;
}

/**
 * The market's fair probability for one leg, or null if the market has nothing
 * to say about it.
 *
 * A named market book is the whole opinion. Two things send it back to the
 * board, exactly as in `runStrategy`: a book cannot be its own market, and a
 * book that is not pricing this game has no view of it. The board's own view
 * leaves the leg's book out — the price being measured does not get to vote on
 * whether it is a good price.
 */
function marketFor(match, side, book, marketBook) {
  if (marketBook && marketBook !== book) {
    const p = bookFairNet(match, side, marketBook, DEVIG);
    if (p !== null) return p;
  }
  return consensus(match, side, { devig: DEVIG, exclude: book })?.pFair ?? null;
}

/**
 * What the ticket pays. `combinedOdds` is the number `Ticket.vue` prints, so
 * it is the number the value is measured on — a value worked out against a
 * price the punter cannot see on the ticket would be arithmetic nobody can
 * check. The product is the fallback for a ticket that arrived without one.
 */
function priceOf(multi, legs) {
  if (Number.isFinite(multi?.combinedOdds)) return multi.combinedOdds;
  const product = legs.reduce((acc, l) => acc * (Number(l?.price) || 0), 1);
  return Math.round(product * 100) / 100;
}

/**
 * Rank every strategy's tickets against the market, best value first.
 *
 * `runs` is [{ strategy, tickets }] — one entry per strategy, each run over
 * the same universe with the same ctx the pad uses. `ctx` is that same runner
 * context: `bookmaker` for the book a Classic leg was priced at, `marketBook`
 * for the Settings answer to "which book is the market".
 *
 * Returns { picks, notes }. A pick is:
 *
 *   { rank, strategy, multi, legs, price, pMarket, fairPrice, valueVsMarket,
 *     chance }
 *
 * `notes` is why a ticket is not in the list — a leg off the board, or a leg
 * the market has no price for. A ticket that cannot be measured is dropped
 * rather than scored on a guess, and the reason is said out loud, because a
 * strategy silently missing from a ranking looks like a ranking that lost it.
 */
export function rankTickets(runs, universe, ctx = {}) {
  const byKey = indexMatches(universe);
  // Absent, or 'consensus', means the whole board — the same reading of the
  // setting `runStrategy` takes.
  const marketBook = ctx.marketBook && ctx.marketBook !== 'consensus' ? ctx.marketBook : null;
  const notes = [];
  const entries = [];

  for (const run of runs ?? []) {
    const strategy = run?.strategy ?? null;
    const label = strategy?.name ?? strategy?.id ?? 'A strategy';

    for (const ticket of run?.tickets ?? []) {
      const legs = ticket?.legs ?? [];
      if (!legs.length) continue;

      const keys = [];
      let pMarket = 1;
      let measured = true;

      for (const leg of legs) {
        const match = matchFor(leg, byKey);
        const side = match ? sideOf(leg, match) : null;
        if (!match || !side) {
          notes.push(`${label}: a leg is not on today's board, so it cannot be ranked`);
          measured = false;
          break;
        }
        const p = marketFor(match, side, bookOf(leg, ctx), marketBook);
        if (!(p > 0)) {
          notes.push(`${label}: the market has no price for a leg, so it cannot be ranked`);
          measured = false;
          break;
        }
        keys.push(`${match.matchKey}:${side}`);
        pMarket *= p;
      }
      if (!measured) continue;

      const price = priceOf(ticket.multi, legs);
      if (!(price > 0)) continue;

      entries.push({
        // Two strategies can land on the same bet — Optimal Legs and Consensus
        // Edge often print the same single — and one bet must not fill two of
        // the four slots. The legs are what makes it the same bet; the price
        // is not, and the better-priced copy is the one that survives because
        // it is the one that sorts higher.
        key: keys.join('|'),
        pick: {
          rank: 0,
          strategy,
          multi: ticket.multi,
          legs,
          price,
          pMarket,
          fairPrice: 1 / pMarket,
          valueVsMarket: pMarket * price - 1,
          chance: pMarket,
        },
      });
    }
  }

  // Value first, then the likelier of two equal-value tickets, then the multi
  // id: an explicit tie-break every step of the way, so the same board always
  // ranks the same way whatever order the strategies were run in.
  entries.sort((a, b) =>
    b.pick.valueVsMarket - a.pick.valueVsMarket
    || b.pick.chance - a.pick.chance
    || String(a.pick.multi?.id ?? '').localeCompare(String(b.pick.multi?.id ?? '')));

  const seen = new Set();
  const picks = [];
  for (const entry of entries) {
    if (seen.has(entry.key)) continue;
    seen.add(entry.key);
    picks.push({ ...entry.pick, rank: picks.length + 1 });
  }

  return { picks, notes: [...new Set(notes)] };
}

/** The four that print. Four because the pad has four slots, and no other reason. */
export function topPicks(picks, n = 4) {
  return (Array.isArray(picks) ? picks : []).slice(0, Math.max(0, n));
}
